import { useMemo } from "react";
import { FILTER_IDS, generateFilterKey, getFilterKeyForBlock } from "@okf/ootils";
import { useES_CountData, useGetAnnotationsLazy } from "@/utils/react-query-hooks/getData";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useAutoGenFilterConfigsFromTpl } from "@/components/analysisModules/utils/useAutoGenFilterConfigsFromTpl";
import { getTplsWithAnnosEnabled } from "@/components/analysisModules/AnnotationExplorer/useGetAnnotationExplorerData";
import { flattenFilterConfig } from "@/components/discovery/FilterModule/utils/flattenFilterConfig";
import { toActiveFilterSource } from "@/components/discovery/FilterModule/utils/toActiveFilterSource";

// Both scopes, as the report's own filters use: an annotation widget can group
// by a document field, and annotations carry `main`, so a document filter
// narrows them too. Module-level so the config hook's memo sees the same array
// every render; an inline literal would rebuild the whole config on each one.
const FILTER_SCOPES = ["doc", "anno"];

/**
 * Called unconditionally on purpose: useAutoGenFilterConfigsFromTpl returns
 * before its own hooks when disabled, so gating it on whether a widget happens
 * to be drillable would reorder hooks when its content type changes.
 */
export const useAnnoFilterConfig = () => {
  const allTpls = useGetQueryData(["allTpls"]);
  const annoTpls = useMemo(
    () => (allTpls ? getTplsWithAnnosEnabled({ allTpls }) : []),
    [allTpls]
  );
  return useAutoGenFilterConfigsFromTpl({ selectedTpls: annoTpls, filterScopes: FILTER_SCOPES });
};

// Keys a dimension saved without a filterKey could have had: widgets configured
// before field options were stamped (1018c6054), and grouped sub-theme options
// ("Personas (Usage-based)"), which never are. Each is re-derived with the
// helper that stamps new options. Some shapes can't be told apart from the
// saved fields alone (an anno rollup keys on FILTER_IDS for self-managed
// tenants, a generated key otherwise), so this lists every candidate and the
// filter config picks: a key no filter has can't match the wrong one.
const legacyFilterKeys = (dim) => {
  if (!dim) return [];
  const { valuePath, rollupType, rollupPath, comp } = dim;
  if (rollupType === "tagType" && Array.isArray(rollupPath)) {
    // FILTER_IDS only for anno rollups: a doc rollup ending in subThemes
    // would otherwise match the annotations' own sub-theme filter.
    return [
      dim.isAnno ? FILTER_IDS[rollupPath[rollupPath.length - 1]] : undefined,
      generateFilterKey({ filterType: "nestedRollupTagType", rollupPath }),
    ];
  }
  if (rollupType === "valuePathType") {
    return [
      generateFilterKey({
        filterType: "rollupValuePathType",
        tagTypeCollectionToRollup: dim.tagTypeCollectionToRollup,
        valuePathInRolledUpCollection: dim.valuePathInRolledUpCollection,
      }),
    ];
  }
  // A grouped sub-theme buckets by the tags under it, so it keys like any anno
  // tag option: annotations.tags.tags -> FILTER_IDS.tags.
  if (dim.isAnnoSubTheme || (dim.isAnno && typeof valuePath === "string" && valuePath.startsWith("annotations.tags."))) {
    return typeof valuePath === "string" ? [FILTER_IDS[valuePath.split(".").pop()]] : [];
  }
  if (typeof valuePath === "string") {
    return [getFilterKeyForBlock({ block: { valuePath, comp }, scope: "tags" })];
  }
  return [];
};

const findDimConfig = (flat, dim) => {
  const keys = dim?.filterKey ? [dim.filterKey] : legacyFilterKeys(dim).filter(Boolean);
  for (const key of keys) {
    const config = flat.find((f) => f?.filterKey === key);
    if (config) return config;
  }
  return undefined;
};

/** Whether every dimension groups by a filter the config has. */
export const canDrill = (dims, filterConfig) => {
  if (!filterConfig) return false;
  const flat = flattenFilterConfig(filterConfig);
  return dims.every((dim) => !!findDimConfig(flat, dim));
};

/**
 * A clicked datapoint pins one bucket per dimension. Each bucket becomes a
 * filter by joining the dimension option's `filterKey` to the flattened filter
 * config — the same join handleVizSegmentClick makes for a clicked bar. Going
 * through the config rather than hand-building a target is what makes rollup
 * (nestedRollupTagType) and Topics (topicClusteringNodeType) dimensions work:
 * their queries are a taxonomy walk on the backend, not a path match.
 *
 * null when any side can't be resolved — better an empty state than a silently
 * unfiltered, and therefore wrong, list.
 */
const toFilters = ({ datum, dims, filterConfig }) => {
  if (!datum || !filterConfig) return null;

  const flat = flattenFilterConfig(filterConfig);
  // A summarized "Others" segment keeps its own bucket's id and display; only
  // the name has been overwritten with the group label.
  const name = datum.name === "Others" && datum.tooltipLabel ? datum.tooltipLabel : datum.name;

  // Ids only: filters match on them (tags as ObjectIds), so a display name
  // would silently match nothing. Heatmap cells carry row/columnValue, bars
  // value (breakdown segments rowValue/breakdownValue).
  const sides = [
    { dim: dims[0], value: datum.rowValue ?? datum.value, display: name },
    { dim: dims[1], value: datum.columnValue ?? datum.breakdownValue, display: datum.tooltipLabel },
  ].slice(0, dims.length);

  const filters = [];
  for (const { dim, value, display } of sides) {
    const config = findDimConfig(flat, dim);
    if (!config || !value) return null;
    filters.push({
      filterId: config.filterId,
      filterKey: config.filterKey,
      target: config.target,
      source: toActiveFilterSource(config.source),
      display: config.display,
      values: [{ value, display }],
    });
  }
  return filters;
};

/**
 * The annotations behind one clicked datapoint: the widget's own filters plus
 * one per pinned bucket. Panel and count both call this; with the same
 * arguments they share a react-query cache entry, so the split costs nothing.
 */
export const useAnnoDrilldown = ({ datum, dims, baseFilters = [], filterConfig }) => {
  const filters = useMemo(
    () => toFilters({ datum, dims, filterConfig }),
    [datum, dims, filterConfig]
  );

  const activeFilters = useMemo(
    () => (filters ? [...baseFilters, ...filters] : []),
    [baseFilters, filters]
  );

  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAnnotationsLazy({
      activeFilters,
      dataSource: "annotations",
      translationConfig: {
        customBlocks: [
          { comp: "TitleInput", props: {}, valuePath: "main.title" },
          { comp: "LexicalTextEditor", props: {}, valuePath: "annotations.fragment" },
        ],
      },
      queryOptions: { enabled: !!filters },
    });

  // getAnnotations only returns a count when the caller passes `needCount`,
  // which the controller doesn't plumb through ("-deprecated-in-this-api-"), so
  // the total comes from the same Elastic count the annotation explorer uses.
  const { data: countData } = useES_CountData({
    contentTypes: ["annotations"],
    activeFilters,
    enabled: !!filters,
  });

  // LexicalTextEditor throws on an empty editor state, so text-less annotations
  // never get a card — same guard the annotation explorer applies.
  const annos = useMemo(() => {
    if (status !== "success") return [];
    return (data?.pages || [])
      .flatMap((page) => page.data || [])
      .filter((d) => d?.annotations?.fragment?.allText?.trim());
  }, [data, status]);

  return {
    filters,
    annos,
    // Once every page is in, the cards are the whole answer: the Elastic count
    // also includes the text-less annotations that never get one.
    total: status === "success" && !hasNextPage ? annos.length : countData?.count,
    status,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
